/**
 * One studio action, as a form — `.docs/20-studio-portal-pages.md` §Before fan-out 2.
 *
 * Given a name it fetches the catalogue, works out the fields (`actionFields.ts`),
 * renders them with session 09's controls, and submits through `useStudioAction`. A
 * page that needs "New task" writes `<ActionForm action="tasks_create" …/>` and
 * nothing else — §DRY watchlist: "`actionFields.ts` is the only field map; no
 * per-page form definitions."
 *
 * Four things it owns, and each is one every hand-written copy would get wrong in a
 * different way:
 *
 *   1. **The money confirmation.** A `$` action opens a `ConfirmDialog` whose button
 *      says the amount and, when the input names one, the reference — `.docs/20`
 *      §Design decisions' rule, and what `pages/studio/rules.test.tsx` asserts across
 *      every page. An action with no amount in its input still confirms: `$` means
 *      "a person should be looking at this", not "there is a number here".
 *   2. **The API's own field errors.** `http/problem.ts` renders a `ZodError` as
 *      `errors: [{ path, message }]`, and each is set on the field it names, so a
 *      refusal lands under the box that caused it. `messageOf` is the sentence for
 *      anything with no path.
 *   3. **`needs_review`.** A 202 is a success with a task reference in it, not an
 *      error (`api/studio.ts` `reviewOf`), and the form links the task.
 *   4. **What it will not draw.** Eight actions are somebody's component
 *      (`actionFields.ts` `HAND_WRITTEN`); this renders the reason rather than half a
 *      form. Reaching that message is a bug in the page, not a state a studio member
 *      can get into, which is why it reads like a note to a developer. **A `schema`
 *      override is the one thing that answers it** — a page that has brought the
 *      schema the published input could not carry has answered the objection, and
 *      `ActionSchemaOverride` is the whole of that argument.
 *
 * The resolver's schema is `actionSchema.ts`'s, built from the fields, and its own
 * header says why it is not the API's: there is no object to share, and rebuilding
 * the API's rules here would be a second validator that refuses things the server
 * would have accepted. The values the form holds and sends are
 * `components/portal/schemaForm.ts`'s, because the client's widget configuration form
 * holds and sends the same ones.
 */

import { useLayoutEffect, useMemo, useRef, useState, useEffect, useId } from 'react'
import type { SubmitHandler } from 'react-hook-form'

import { toMajorUnits, type Currency, type Money } from '@fareast/shared'

import { isPressOn, useLandedWrites, useWritesOnTheirWay } from '../../api/hooks.js'
import {
  Unanswered,
  changedByOthers,
  fieldErrorsOf,
  messageOf,
  problemPaths,
} from '../../api/problem.js'
import {
  reviewOf,
  runtimeBody,
  useCatalogue,
  useStudioAction,
  useStudioRead,
  type StudioActionName,
} from '../../api/studio.js'
import { useFormat } from '../../format/useFormat.js'
import { Button, ButtonRow, type ButtonVariant } from '../portal/Button.js'
import { HALF_TYPED_ERROR, halfTypedIn, settledBox, useZodForm } from '../portal/Form.js'
import { Stack } from '../portal/Layout.js'
import { ConfirmDialog } from '../portal/Overlay.js'
import { isRefField, type FieldSpec, type SchemaNode } from '../portal/schemaFields.js'
import {
  amountOf,
  cannotBeEmptied,
  changesOf,
  formValuesOf,
  isBlank,
  isMoneyValue,
  wireValuesOf,
  type FormValues,
} from '../portal/schemaForm.js'
import { Notice } from '../portal/Status.js'
import { Prose } from '../portal/Text.js'
import { ActionField, NOBODY } from './ActionField.js'
import {
  fieldsFor,
  HAND_WRITTEN,
  UNCHANGED_SINCE_FIELD,
  WAS_FIELD,
  wasFieldsOf,
} from './actionFields.js'
import { queueForService } from './queueForService.js'
import { schemaFor } from './actionSchema.js'
import { NeedsReviewNotice } from './NeedsReviewNotice.js'
import { changedWords, HELD_FIELDS, isNewsSince, wordsFor } from './taskWords.js'

/**
 * A schema the **page** brought, for an action whose published input could not carry
 * one — and the field the drawn values are sent as.
 *
 * One action uses it: `widgets_set_config`, whose `config` is a map validated against
 * the schema a widget snapshotted from its type when it was created. That schema is a
 * row in the database, so it is in no catalogue and there is nothing generic
 * `_catalogue`'s `inputs` could have said about it
 * (`.plans/ai-product-placer/plan/evidence/portal.md` §D.4).
 *
 * **The override is the schema, never the action.** `entry` — the title and the money
 * marker — still comes from the catalogue, because those are facts about the tool
 * being called and a page does not get to restate them. The alternative the plan
 * records and refuses is publishing every widget's snapshot into `inputs`, which is a
 * per-widget schema on a per-action key.
 *
 * `under` is what makes the body the action's rather than the schema's: the fields
 * are `config`'s properties and the call takes `{ widget, config: { … } }`, so the
 * drawn values are nested on the way out and the API's own `config.<key>` field
 * errors are unnested on the way back.
 */
export type ActionSchemaOverride = {
  /** The JSON Schema the fields are drawn from. */
  readonly node: SchemaNode
  /** The action's own field the drawn values are sent as — `config`. */
  readonly under: string
}

export type ActionFormProps = {
  readonly action: StudioActionName
  /** The page's own schema for this action's one map field. Almost always absent. */
  readonly schema?: ActionSchemaOverride | undefined
  /**
   * Fields the page sets and the reader does not see — the client a dialog was
   * opened from, the reference of the row a button is on. Merged over the form's own
   * body, so a page cannot be overridden by a field it also chose to render.
   */
  readonly fixed?: Readonly<Record<string, unknown>> | undefined
  /**
   * True where the page's own condition (`fixed.if_was`), once refused, may be
   * **overridden by pressing again**.
   *
   * A hand-over may (`AssignDialog`, and the bulk bar's): giving a task to somebody
   * is a decision about the task whoever holds it, the page offers it on anybody's, and
   * a reader told that a colleague picked it up first can still mean it. So may the
   * bulk bar's Due and Queue windows, which set a value: the same second Save an edit
   * window offers over a box somebody else changed (`clashed`).
   *
   * It is also what says the condition is **not a premise**, which is what lets a write
   * of the reader's own amend it (`useOpenedFrom`).
   *
   * A **move** may not, and is the rule. The condition there is the page's *premise*:
   * the move was offered because the task was the reader's and stood where the button
   * says. Refused, the premise is false, and "do it anyway" is not the same act — the
   * page behind an open window is not read again, so the reader has not seen what the
   * task is now, and `tasks_complete` on a task that has gone to review is its
   * approval: *Mark done*, pressed twice, approved a review nobody had read. So the
   * condition stays on every press, and the window says to shut it and look.
   */
  readonly insist?: boolean | undefined
  /**
   * The stamp (`updated_at`) of the row `fixed.if_was` was read from. A task window
   * gets the two together (`taskWords.ts` `moveWindowOf`, `editWindowOf`).
   *
   * It is what tells an answer that is **news** from one the row had already shown
   * (`useOpenedFrom`). Without one, every answer of the reader's own is taken for news.
   */
  readonly stamp?: string | undefined
  /**
   * Choices a picker must not offer **here**, by field name — `{ assignee: ['none'] }`.
   *
   * For a choice the field's own list has and this window's subject cannot take: the
   * Assign window on a task that cannot be left with nobody. Offered, it was chosen
   * and refused every time, with a line above the form to say it would be.
   */
  readonly without?: Readonly<Record<string, readonly string[]>> | undefined
  /**
   * When set, only these fields are drawn (after `fixed` is removed). Used by the
   * list / board / detail cell editors so "Change priority" is one box, not the
   * whole `tasks_update` surface.
   */
  readonly only?: readonly string[] | undefined
  /**
   * Fields the published schema leaves optional that this form must not submit blank.
   * A bulk editor copies the lead task's answer onto the rest, and a blank optional
   * field is omitted from the body — so a blank would copy the lead's **old** value.
   */
  readonly required?: readonly string[] | undefined
  /** Fields shown, filled in and editable. */
  readonly defaults?: Readonly<Record<string, unknown>> | undefined
  /**
   * True when `defaults` are a **starting point** for something new, rather than a
   * row as it is stored.
   *
   * It decides what an emptied box means. Over a stored row — every dialog that edits
   * one, and the rule unless a page says otherwise — a field left out of the body is
   * a field left *unchanged*, so a box the reader emptied is sent as a clear, or
   * refused where its schema has none (`schemaForm.ts` `clearedOf`). Over nothing —
   * New Task, opened with the board's filters already filled in — leaving the field
   * out **is** the answer: a task raised with no client is studio work, and
   * `tasks_create` takes neither a `null` nor an `''` for one.
   *
   * A form with no `defaults` has no use for it: a box that started blank is left out
   * either way.
   */
  readonly suggested?: boolean | undefined
  /**
   * Two-column field layout on a wide dialog (#39). Phone stays one column via CSS.
   * Pair with `fullWidth` for title / long text / references (#40).
   */
  readonly columns?: 2 | undefined
  /** Field names that span both columns when `columns` is 2. */
  readonly fullWidth?: readonly string[] | undefined
  /** The submit button's words. "Create the task", "Record it". */
  readonly submitLabel: string
  /** Its colour. `primary` unless the move it commits cannot be undone. */
  readonly submitVariant?: ButtonVariant | undefined
  /**
   * Run when the call came back with the action's own answer, not a review task.
   *
   * **Its own exceptions are its own.** It runs after the request has been answered
   * and outside the guard round it, so a page whose `onDone` throws — several parse
   * the output with a schema — sees that throw as an uncaught error rather than
   * having it read as a refusal from the API.
   */
  readonly onDone?: ((output: unknown) => void) | undefined
  /**
   * Run instead of a request when Save is pressed over a stored row with **nothing
   * changed** — a form that sends what changed (`changesOf`) has nothing to send.
   * `ActionDialog` closes the window; left out, the form says so in a line of its own.
   */
  readonly onUnchanged?: (() => void) | undefined
  /**
   * Told whether the form holds anything the reader would lose — true once a box
   * differs from what it opened with, false again when it is put back or the form
   * goes away. `ActionDialog` is the caller: a click beside a window is how a
   * half-written note used to be thrown away without a word.
   */
  readonly onDirtyChange?: ((dirty: boolean) => void) | undefined
  /**
   * Told `true` as the form's own write goes out, and `false` once it has been
   * answered — yes or no — and everything it touched has been read again; before
   * `onDone`. `ActionDialog` is the caller: the form is where a refusal is said, and a
   * window shut while its write was on its way took the refusal with it.
   */
  readonly onSending?: ((sending: boolean) => void) | undefined
  /** A line above the fields, when the dialog's title is not enough. */
  readonly children?: React.ReactNode | undefined
  /**
   * A second control beside the commit — a way out that is not a field.
   *
   * Given a **function**, it is handed what the form holds right now. Quick create
   * is the one caller that needs it: "More details" leaves this form for the full
   * one, and a title typed here has to arrive there rather than being thrown away
   * at the door (#33).
   *
   * **And whether the form is waiting** (`working`), so that a way out of the form is
   * not one while its write is on its way: pressed then, "More details" took the form
   * away with the answer still to come — a refusal said nowhere, or a task created and
   * the window the reader had just begun typing in shut under them. **Or has had no
   * answer**: this form is then the one thing that can find out what became of its
   * press without making it twice, and the form "More details" opens has a button of
   * the same name that makes it again.
   */
  readonly beside?:
    React.ReactNode | ((values: FormValues, working: boolean) => React.ReactNode) | undefined
  /**
   * Put the cursor in the first field when the form mounts.
   *
   * Off by default. Radix hands focus to the first focusable thing, which is the
   * dialog's Close, and for most forms that is right — the reader is about to read
   * before they type. Quick create is the exception (#33): the window *is* one box,
   * and "Title + Enter" is not one action if it starts with a Tab.
   *
   * `session 24`'s rule is untouched: this focuses a **field**, never the button
   * that commits.
   */
  readonly autoFocusFirstField?: boolean | undefined
  /** A question to answer before these values are sent, or null to send them at once.
   *  Not asked on a `$` action: its own money confirmation is the one question there. */
  readonly confirm?: ((values: FormValues) => ActionConfirm | null) | undefined
}

/** What a form asks before it runs: a heading, one sentence, and the button that goes on. */
export type ActionConfirm = {
  readonly title: string
  readonly says: string
  readonly confirmLabel: string
}

export function ActionForm(props: ActionFormProps) {
  const catalogue = useCatalogue()
  // Before either early return: the window is open whether or not it can draw yet.
  const opened = useOpenedFrom(props.action, props.fixed, props.stamp, props.insist !== true)

  /*
   * **From the list as it was last read, where it has been read at all.** The list is
   * read again after every write to its own module — an agent's scopes, a setting —
   * and a re-read that failed used to replace the form with the sentence below: the
   * window still open, and what had been typed in it gone, for a list the form had
   * already been drawn from.
   */
  if (catalogue.data === undefined) {
    if (catalogue.isError) {
      return <Notice need>We could not read the tool list, so this form cannot be drawn.</Notice>
    }
    return <p className="empty">Reading what this can do…</p>
  }

  const override = props.schema
  const reason = HAND_WRITTEN[props.action]
  if (reason !== undefined && override === undefined) {
    return (
      <Notice need role="alert">
        <b>{props.action}</b> is not a generic form. {reason}
      </Notice>
    )
  }

  const entry = catalogue.data.actions.find((one) => one.name === props.action)
  const declared =
    override === undefined
      ? fieldsFor(props.action, catalogue.data.inputs[props.action])
      : fieldsFor(props.action, override.node, { override: true })
  /*
   * A `fixed` field is not drawn.
   *
   * "Fields the page sets and the reader does not see" was the promise and not the
   * behaviour: every declared field was rendered and `fixed` was merged over the body
   * afterwards, so a dialog opened from a client's own page showed an empty **Client**
   * box whose value was then thrown away. Filtering here rather than in the renderer
   * is what also keeps the field out of the resolver's schema — otherwise a required
   * one the page had already answered refuses a submit with no visible error.
   *
   * `only` then narrows further: a Due cell must not re-open Title / Queue / Service.
   */
  const unlocked = declared?.filter((field) => !(field.name in (props.fixed ?? {}))) ?? null
  const allowed = props.only === undefined ? null : new Set(props.only)
  const mustAnswer = new Set(props.required ?? [])
  /*
   * Whether **emptying a box is a change to make** here — and false in the two
   * places it is not, so that neither sends anything it did not send before.
   *
   * `suggested` is the page's own word for the first: the defaults are a starting
   * point and nothing is stored behind them. An override is the second, and it needs
   * no telling. The drawn values there are **one field of the body, sent whole** —
   * `widgets_set_config` replaces a configuration rather than merging into it — so a
   * key left out of it is a key the new configuration does not have, which is
   * already the clear. A `null` in its place would be a value the widget's own
   * snapshot refuses.
   */
  const clears = override === undefined && props.suggested !== true
  /*
   * A field the page says must be answered is drawn as a required one (`required`).
   * The other half of "must not be sent blank" — a box that opens with a value its
   * schema cannot take away — is the form's own to decide, from what it opened
   * holding (`ActionFormFields` `opened`).
   */
  const fields =
    unlocked === null
      ? null
      : (allowed === null ? unlocked : unlocked.filter((field) => allowed.has(field.name))).map(
          (field) => (mustAnswer.has(field.name) ? { ...field, optional: false } : field),
        )
  if (fields === null || entry === undefined) {
    return (
      <Notice need role="alert">
        <b>{props.action}</b> has an input this form cannot draw. Give it an entry in{' '}
        <code>components/studio/actionFields.ts</code>.
      </Notice>
    )
  }

  /*
   * Whether this form sends **what changed, and what from**, rather than every box.
   *
   * True where all three hold: the defaults are a stored row's (`clears`, and some
   * were given); the action can be told what a field held (`if_was` in its published
   * input); and every box drawn is one it can be told about. `tasks_update` is the
   * one where that is so today. A hand-over's and a move's `if_was` name the holder
   * and the state, which no box draws — the page sends those through `fixed` — and the
   * last clause is what keeps their forms, and any form with a box the condition does
   * not cover, sending exactly what they always sent.
   */
  const wasFields = wasFieldsOf(catalogue.data.inputs[props.action])
  const expects =
    clears &&
    props.defaults !== undefined &&
    fields.length > 0 &&
    fields.every((field) => wasFields.has(field.name))

  /*
   * The fields are the key.
   *
   * `useForm` reads `defaultValues` once, at mount, so the inner component may only
   * exist when the shape is known — which is exactly what the two early returns
   * above guarantee. Remounting on a shape change is free (the catalogue is stale
   * for an hour) and is what stops a form drawn for `tasks_create` keeping
   * `expenses_record`'s state when a page swaps the action in a dialog.
   */
  return (
    <ActionFormFields
      key={props.action}
      {...props}
      fields={fields}
      clears={clears}
      expects={expects}
      money={entry.marker === '$'}
      title={entry.title}
      openedFrom={opened.from}
      movedOn={opened.moved}
    />
  )
}

/**
 * What the page said the change is made **from** (`fixed.if_was` — who held the task
 * and where it stood, `taskWords.ts` `moveOf`), taken **once, when the window opens**.
 *
 * A page builds `fixed` from the row on every render, and the row can change under an
 * open window: a read already on its way when the window opened lands, and the row
 * now says a colleague holds the task. Read off the prop at the moment of the press,
 * the condition was then *theirs* — it passed, and the move the window was opened to
 * make on the reader's own task was made on the colleague's. A condition is what the
 * window was opened over, or it is not one.
 *
 * **Here and not in the fields**, which are not drawn until the tool list has been
 * read: with a cold catalogue the window says "Reading what this can do…" for a
 * moment, and a condition taken when the fields mount was taken after whatever landed
 * in that moment.
 *
 * **A premise is never amended.** A move's condition is the page's premise — the move
 * was offered because the task was the reader's and stood where the button says — and
 * it stays what the window opened over whatever happens, the reader's own doing
 * included. It was tried the other way: Start, pressed on a row, is answered and then
 * read again, a window opened in that half-second was refused for the reader's own
 * move, and so the move's own answer was made the condition. But a move's answer is a
 * *different state*, and the same window over a different state can be a different
 * act: "Send for review", then "Mark done" from the Status cell before the first had
 * landed, was amended to "in review" — where `tasks_complete` **is the approval** — and
 * a button that said Mark done approved the reader's own review. So where a move of
 * the reader's own lands under a premise and changes it, the window says so at once
 * (`moved`), in true words, and sends nothing: it is shut and opened again, from the
 * task as it stands, by whichever control the page offers there now.
 *
 * **A condition that is not a premise is amended by one thing only: a write of the
 * reader's own, from that write's own answer** (`ownMoveLanded`). Not a premise is a
 * condition the page lets be pressed through (`insist`): a hand-over, which is a
 * decision about the task whoever holds it, and a value, which means the same whatever
 * it replaces. It was tried by having the condition follow the *page* until such a
 * write had settled, and what a write's re-read brings back is the row as it *is*: a
 * date typed on a card and the card's Who chip clicked is one click, the save was
 * always on its way as the window opened, and a colleague who had taken the task since
 * the card was drawn came back with it and became the condition. So the page is never
 * asked. A write that lands says in its own answer where it left the task — and it
 * only landed because the task was where the reader saw it, or it would have been
 * refused itself. A date saved under a window about who holds the task, a comment, a
 * colleague's anything: the condition stays what the window opened over.
 *
 * **And only where that answer is news** (`taskWords.ts` `isNewsSince`), for the
 * amendment and for the sentence alike. A write "lands" when everything it touched has
 * been read again, and the reads come back one at a time: the row can already show the
 * reader's move *and what a colleague did after it* while the move is still waiting
 * for the others. A window opened over that row has seen more than the answer says.
 * So the condition is taken with the stamp of the row it was read from (`stamp`), and
 * an answer that is not later than it changes nothing.
 *
 * **A condition belongs to what the window is about**, so it is taken again when that
 * changes — by action, because a page may swap the action under a mounted form, and
 * by every other `fixed` field, because a window may be handed another task while it
 * is open: the first one's condition, sent with the second one's reference, was
 * refused as "somebody else changed it" when nobody had — or, where the two happened
 * to agree, passed over a change nobody had seen.
 */
function useOpenedFrom(
  action: string,
  fixed: Readonly<Record<string, unknown>> | undefined,
  stamp: string | undefined,
  premise: boolean,
): { readonly from: unknown; readonly moved: readonly string[] | null } {
  const now = fixed?.[WAS_FIELD]
  const about = subjectOf(fixed)
  const [pin, setPin] = useState<Pin>(() => ({ action, about, from: now, stamp, moved: null }))
  const same = pin.action === action && pin.about === about
  if (!same) setPin({ action, about, from: now, stamp, moved: null })
  const from = same ? pin.from : now
  const moved = same ? pin.moved : null
  useLandedWrites((sent, answer) => {
    /*
     * Judged against the pin **as it is when this is applied**, not as it was when the
     * window was last drawn: two writes of the reader's own can land in one turn, and
     * the second, weighed against a stamp the first had already moved on from, put an
     * older answer over a newer one.
     */
    setPin((held) => {
      const mine = held.from
      if (held.action !== action || held.about !== about || !isRecord(mine)) return held
      const landed = ownMoveLanded(fixed, mine, sent, answer)
      if (landed === null || !isNewsSince(held.stamp, landed.stamp)) return held
      const at = landed.stamp ?? held.stamp
      if (!premise) return { ...held, from: landed.from, stamp: at, moved: null }
      const changed = Object.keys(landed.from).filter(
        (name) => !Object.is(landed.from[name], mine[name]),
      )
      if (changed.length === 0) return { ...held, stamp: at }
      return { ...held, stamp: at, moved: [...new Set([...(held.moved ?? []), ...changed])] }
    })
  }, isRecord(from))
  return { from, moved }
}

/** What a window was opened over, and what it was opened about. */
type Pin = {
  readonly action: string
  /** Every `fixed` field but the condition, as one comparable value. */
  readonly about: string
  readonly from: unknown
  /** The stamp of the row `from` was read from, or of the newest answer heard since. */
  readonly stamp: string | undefined
  /** Which fields of a premise the reader's own move has changed since, or null. */
  readonly moved: readonly string[] | null
}

/**
 * What a window is **about**: the `fixed` fields that are not its condition. A field
 * given as `undefined` is left out, as the body leaves it out: that is not one sent as
 * null, and the two are different requests.
 */
function aboutOf(
  fixed: Readonly<Record<string, unknown>> | undefined,
): readonly (readonly [string, unknown])[] {
  return Object.entries(fixed ?? {}).filter(
    ([name, value]) => name !== WAS_FIELD && value !== undefined,
  )
}

/** The same as one comparable value: two renders are about one thing when it is equal. */
function subjectOf(fixed: Readonly<Record<string, unknown>> | undefined): string {
  return JSON.stringify(aboutOf(fixed))
}

/**
 * What a form says of its own press that had **no answer** — or null for anything else.
 *
 * Not "look at how things stand before you try it again", which is what a button says:
 * the page behind a window is not read while the window is open. Here there is
 * something better to say: the same submit pressed again goes out under the first
 * one's key (`useStudioAction` `again`), so it finds out what became of the first and
 * is made only if that one never arrived. **"Without changing anything"** is the whole
 * of that condition: a box changed first makes another request, with a key of its own.
 * Where pressing could not be that press it offers nothing: the window is out of date
 * and sends nothing (`offer` false), or is about something else now (`elsewhere`) — and
 * then it names the press, and its task, rather than calling it "this".
 */
function unansweredSaid(
  error: unknown,
  submitLabel: string,
  pressed: { readonly offer: boolean; readonly elsewhere: Unknown | null },
): string | null {
  if (!(error instanceof Unanswered)) return null
  const what = error.how === 'unreached' ? 'The portal could not be reached' : 'No answer came back'
  if (pressed.elsewhere !== null) {
    return `${what} when you pressed ${submitLabel}${onTask(pressed.elsewhere)}, so that may or may not have been done.`
  }
  const happened = `${what}, so this may or may not have been done.`
  if (!pressed.offer) return happened
  const again = `press ${submitLabel} again without changing anything to find out: it will not be done twice.`
  return error.how === 'unreached'
    ? `${happened.slice(0, -1)}. Check your connection, then ${again}`
    : `${happened} P${again.slice(1)}`
}

/** A press of this form that had no answer: what the window was about, and its task. */
type Unknown = { readonly subject: string; readonly taskRef: string | null }

/** " on TSK-0059", for a press made on a task. */
function onTask(press: Unknown): string {
  return press.taskRef === null ? '' : ` on ${press.taskRef}`
}

/** The answer's own version, which the whole-task stamp is a copy of (`CellEdit` `stampOf`). */
const VERSION_FIELD = 'updated_at'

/** The whole-task condition an Undo sends; a move that says only that is still a move. */
const STAMP_FIELD = UNCHANGED_SINCE_FIELD

/**
 * A press of several writes about the same thing that is still on its way
 * (`api/hooks.ts` `asOnePress`) — waited for, by a window with a condition, as each of
 * its writes is, so that the window does not go on from what the first of them left.
 */
function isPressUnder(
  fixed: Readonly<Record<string, unknown>> | undefined,
  from: unknown,
  sent: unknown,
): boolean {
  const about = aboutOf(fixed)
  return isRecord(from) && about.length > 0 && isPressOn(sent, Object.fromEntries(about))
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Whether a write is **a move of the reader's own on the thing a window is about** —
 * told by what it sent, because a write does not carry its tool's name.
 *
 * **About the same thing**: it names every `fixed` field the window does, the
 * condition aside (for a task window, the task).
 *
 * **And one whose answer is the reader's own doing**, in the fields this window's
 * condition names (`from`). Three shapes are, and each for its own reason:
 *
 *   - it said what it was made from **in this window's own terms** — `if_was` over the
 *     same fields. It landed, so those fields were as the reader saw them, and what
 *     they are now is what it made them. A hand-over, every move with a window, and a
 *     date or a queue saved from a row.
 *   - it **wrote every one of those fields**. What they are now is what it wrote,
 *     whatever it was made from: the date an Undo puts back under its stamp, and a
 *     value an edit window saves a second time, over a box somebody else had changed.
 *   - it is **a move that names neither** who holds the task nor where it stands, and
 *     those are what the window's condition is (`HELD_FIELDS`). `tasks_claim`, which
 *     says nothing but the task and refuses itself over exactly those; and an Undo's
 *     way back under the whole-task stamp (`if_unchanged_since`) — the give-back, or
 *     the name put back — which landed because nothing at all had changed since the
 *     reader's own take, and that take was made from the row.
 *
 * Under a window whose condition is anything else — a date, a queue — the third shape
 * speaks for nothing. A claim's answer carries the date a colleague set a moment ago
 * as readily as the reader's; and a give-back's stamp says nothing has changed since
 * the reader's last write, not that the reader had *seen* what that write answered
 * with, because the re-read after it can fail.
 *
 * Anything else — a note, minutes logged, a hand-over pressed through with no
 * condition, a field saved that the window is not about — may answer with somebody
 * else's change in it, and is not listened to.
 */
export function isOwnMove(
  fixed: Readonly<Record<string, unknown>> | undefined,
  from: unknown,
  sent: unknown,
): boolean {
  if (!isRecord(from) || !isRecord(sent)) return false
  const names = Object.keys(from)
  const about = aboutOf(fixed)
  if (names.length === 0 || about.length === 0) return false
  if (!about.every(([name, value]) => sent[name] === value)) return false

  const madeFrom = sent[WAS_FIELD]
  if (isRecord(madeFrom) && names.every((name) => name in madeFrom)) return true

  // What it wrote: everything it sent but the thing itself and the two conditions.
  const wrote = Object.keys(sent).filter(
    (name) =>
      name !== WAS_FIELD && name !== STAMP_FIELD && !about.some(([known]) => known === name),
  )
  if (names.every((name) => wrote.includes(name))) return true

  const holding =
    names.length === HELD_FIELDS.length && HELD_FIELDS.every((name) => names.includes(name))
  if (!holding || !wrote.every((name) => names.includes(name))) return false
  return STAMP_FIELD in sent || (wrote.length === 0 && !(WAS_FIELD in sent))
}

/**
 * Such a move, **or a press of several about the same thing** (`isPressUnder`): what
 * a window with that condition, a draft over it, and a control that sends at once all
 * wait for before they send.
 */
export function isOwnWriteUnder(
  fixed: Readonly<Record<string, unknown>> | undefined,
  from: unknown,
  sent: unknown,
): boolean {
  return isOwnMove(fixed, from, sent) || isPressUnder(fixed, from, sent)
}

/**
 * True while one is on its way (`api/hooks.ts` `useWritesOnTheirWay`).
 *
 * A window waits with its buttons (`landing`). **A control that sends at once waits
 * by being shut**: sent in the half-second before the page had been read again, a
 * hand-over picked in a row's Who cell just after Start in its Status cell went out
 * from what the row still said and was refused — "somebody else changed where it
 * stands", of the reader's own Start.
 */
export function useOwnWriteOnItsWay(
  fixed: Readonly<Record<string, unknown>> | undefined,
  from: unknown,
): boolean {
  return useWritesOnTheirWay((sent) => isOwnWriteUnder(fixed, from, sent))
}

/**
 * Where such a move left the thing — the fields the window's condition names, read off
 * the move's own answer, and that answer's stamp where it carries one — or null when
 * the write that landed is not one.
 */
export function ownMoveLanded(
  fixed: Readonly<Record<string, unknown>> | undefined,
  from: unknown,
  sent: unknown,
  answer: unknown,
): {
  readonly from: Readonly<Record<string, unknown>>
  readonly stamp: string | undefined
} | null {
  if (!isRecord(from) || !isOwnMove(fixed, from, sent)) return null
  const names = Object.keys(from)
  const places = [answer, ...(isRecord(answer) ? Object.values(answer) : [])]
  const found = places.find(
    (one): one is Readonly<Record<string, unknown>> =>
      isRecord(one) && names.every((name) => name in one),
  )
  if (found === undefined) return null
  const stamp = found[VERSION_FIELD]
  return {
    from: Object.fromEntries(names.map((name) => [name, found[name]])),
    stamp: typeof stamp === 'string' ? stamp : undefined,
  }
}

/* --------------------------------------------------------------- the form */

type FieldsProps = ActionFormProps & {
  readonly fields: readonly FieldSpec[]
  /** True where a box that opened with a value and is blank now is sent as a clear. */
  readonly clears: boolean
  /** True where only what changed is sent, with what it was changed from. */
  readonly expects: boolean
  /** True for a `$` action: it confirms before it runs. */
  readonly money: boolean
  /** The action's own title, which is what the confirm dialog is headed with. */
  readonly title: string
  /** `fixed.if_was` as it was when the window opened (`useOpenedFrom`). */
  readonly openedFrom: unknown
  /**
   * The fields of this window's premise that a move of the reader's own has changed
   * since it opened, or null. Not null, the window is out of date and sends nothing.
   */
  readonly movedOn: readonly string[] | null
}

function ActionFormFields({
  action,
  fields: declared,
  clears,
  expects,
  fixed,
  openedFrom,
  movedOn,
  insist,
  without,
  defaults,
  submitLabel,
  submitVariant,
  onDone,
  onUnchanged,
  onDirtyChange,
  onSending,
  money,
  title,
  schema: override,
  columns,
  fullWidth,
  children,
  beside,
  autoFocusFirstField,
  confirm,
}: FieldsProps) {
  const span = new Set(fullWidth ?? [])
  /*
   * What the boxes opened holding, **taken once** — in the same render the form takes
   * its own values in, and for the same reason: `useForm` reads `defaultValues` at
   * mount and never again.
   *
   * It used to be read off the `defaults` prop on every render, and the two came
   * apart the moment the page behind the window was read again. A task with no money
   * reference, the *Change money* window open, and a colleague sets `REQ-119`: the
   * boxes still hold what they opened with, a blank; `defaults` now says `REQ-119`;
   * and a blank box over a filled default is what "somebody emptied it" looks like —
   * so Save sent `money_ref: null` and wiped a value the reader had never been
   * shown. What was emptied is a fact about this form's own boxes, and those are as
   * old as the mount.
   */
  const [opened] = useState(() => (clears ? defaults : undefined))
  /*
   * A box that opened with a value its schema has no way to take away — a title, a
   * queue — is drawn as a required one, where leaving it out would read as
   * *unchanged* and the reader would be told nothing (`schemaForm.ts`
   * `cannotBeEmptied`). Required is what makes the refusal the field's own sentence
   * under the field's own box, and what takes "(optional)" off a label that was
   * never true of it here.
   */
  const fields = useMemo(
    () =>
      declared.map((field) =>
        cannotBeEmptied(field, opened) ? { ...field, optional: false } : field,
      ),
    [declared, opened],
  )
  const resolver = useMemo(() => schemaFor(fields), [fields])
  const form = useZodForm<FormValues, FormValues>(resolver, {
    defaultValues: formValuesOf(fields, defaults),
  })
  /*
   * Whether the reader has anything here to lose, said to whoever holds the window.
   *
   * react-hook-form's own `isDirty` — the boxes against what they opened with — so a
   * word typed and deleted again is not unsaved input, and a form opened and only
   * looked at is clean. Read during render, which is what subscribes to it. The
   * clean-up says `false`: a form that has gone away holds nothing, and the next one
   * opened in the same window starts clean rather than inheriting this one's answer.
   */
  const dirty = form.formState.isDirty
  useEffect(() => {
    onDirtyChange?.(dirty)
    return () => {
      onDirtyChange?.(false)
    }
  }, [dirty, onDirtyChange])
  const [pending, setPending] = useState<FormValues | null>(null)
  const [asking, setAsking] = useState<{
    readonly values: FormValues
    readonly ask: ActionConfirm
  } | null>(null)
  /*
   * The question is asked **in the form**, where its submit button was, not in a
   * second window: every form that asks one is already inside a dialog, and a dialog
   * opened over a dialog left the first one's title and button showing round it.
   * Focus goes to Not yet while it is asked (never to the button that commits), and
   * back to the submit button when it is answered.
   */
  const notYetId = useId()
  const submitId = useId()
  const wasAsking = useRef(false)
  useEffect(() => {
    if (asking !== null) {
      wasAsking.current = true
      document.getElementById(notYetId)?.focus()
      return
    }
    if (!wasAsking.current) return
    wasAsking.current = false
    document.getElementById(submitId)?.focus()
  }, [asking, notYetId, submitId])
  // A field changed after the question was asked: the question was about other values.
  useEffect(() => {
    const watching = form.watch(() => {
      setAsking(null)
    })
    return () => {
      watching.unsubscribe()
    }
  }, [form])
  /** True when every one of the API's own errors is showing under a field already. */
  const [covered, setCovered] = useState(false)
  /*
   * The boxes somebody else has been found to have changed while this was open.
   *
   * The reader has been told, under each of them, and what they do next is a
   * decision: a second Save puts what the box holds in its place, on no condition
   * (`schemaForm.ts` `changesOf` `decided`).
   */
  const [clashed, setClashed] = useState<ReadonlySet<string>>(() => new Set())
  /*
   * A refusal of the same kind about something this form does not draw — who held
   * the task and where it stood, sent by the page through `fixed`. Said at the foot,
   * in the words for what changed (`taskWords.ts` `changedWords`), or null while
   * there is nothing of the kind to say.
   */
  const [overtaken, setOvertaken] = useState<{
    readonly about: string
    readonly words: string
  } | null>(null)
  /**
   * What that has been said of **where the page lets it be insisted on** (`insist`):
   * the condition in `fixed` is not sent again for it. For a move it is never set.
   *
   * **Kept by what the window was about when the refused press was made**, and true
   * of nothing else. A window may be handed another task while it is open
   * (`useOpenedFrom`), before the refusal or — the request being on its way — after
   * the press and before the answer: "press again to do it anyway" was said of the
   * first task, and a flag that only said *yes* sent the second press for the second
   * one with no condition at all, over whatever a colleague had done to a task the
   * reader had not been refused on.
   */
  const [decidedFor, setDecidedFor] = useState<string | null>(null)
  const subject = subjectOf(fixed)
  const decided = decidedFor === subject
  /** Save pressed with nothing changed, and nobody to tell but the reader. */
  const [unchanged, setUnchanged] = useState(false)
  /** The last press that had no answer: offered again only while the window is about it. */
  const [unanswered, setUnanswered] = useState<Unknown | null>(null)

  const format = useFormat()
  /*
   * `again`: this form's own submit, pressed again as it stands after a press that
   * had no answer, is that press — and goes out under its key, so the door answers
   * with what became of the first and makes it only if it never arrived. The sentence
   * the form says of such a press is what tells the reader so (`unansweredSaid`).
   * (True of every action a form is drawn for. The few the door stores no answer for
   * take no key at all, and refuse a call that carries one — which every call made
   * here does. And `tasks_heartbeat`, which takes the key and runs every time, does
   * nothing for a person: their task has no lease to extend.)
   */
  const run = useStudioAction(action, { again: true })
  /*
   * This form's own press has had no answer, and what became of it is this form's to
   * find out. A way out of the form is not one for as long: it would take away the
   * one control that can ask without doing the thing twice (`beside`). **The hook
   * says so** (`unsettled`), and not this form's last error: a press made again that
   * was never sent, or refused because the session had ended, is another error over
   * the same unanswered first.
   */
  const unsettled = run.unsettled
  const review = reviewOf(run.data)
  /*
   * A move of the reader's own on the same thing that has not landed yet — Start,
   * pressed on the row a moment before this window was opened. What it leaves decides
   * what this window is (`useOpenedFrom`: its condition amended, or the window out of
   * date), so nothing is sent until it has: pressed in that half-second, the window
   * sent what it was opened over and was refused for the reader's own move. This
   * form's own write is one such, which is the button it already waits for — and both
   * buttons say the same word while they do: one that is merely grey, under its
   * ordinary label, reads as a window that cannot be used.
   */
  const landing = useOwnWriteOnItsWay(fixed, openedFrom)
  const working = run.isPending || landing
  /*
   * Out of date: the reader's own move changed what this window was opened for.
   *
   * **Not this window's own**, which is such a move too and is the one thing it was
   * opened to do: once it has gone through there is nothing here to be out of date
   * about. A window shuts on it (`ActionDialog`), and a form that stays is not told it
   * can no longer be used because it has just been used. Remembered (`made`), and not
   * read off the last press: `run.isSuccess` is true of that press only, and a second
   * one — refused, say — brought the sentence back about the first.
   */
  const [made, setMade] = useState(false)
  const stale = movedOn !== null && !made

  /*
   * Choosing a service on `tasks_create` fills the queue and the billing default
   * (#44). Queue from the code's keywords; needs_review from the rate card's
   * category (project work is reviewed by a person). Only when the form draws
   * those fields — a page that fixed them keeps its answer.
   */
  const serviceCode = form.watch('service_code')
  const catalog = useStudioRead('catalog_list_services', {}, { inline: true })
  useEffect(() => {
    if (action !== 'tasks_create') return
    if (typeof serviceCode !== 'string' || serviceCode === '') return
    if (fields.some((field) => field.name === 'queue')) {
      const queue = queueForService(serviceCode)
      if (queue !== null) form.setValue('queue', queue, { shouldDirty: true })
    }
    const category = catalog.data?.services.find((one) => one.code === serviceCode)?.category
    if (category === undefined) return
    if (fields.some((field) => field.name === 'needs_review')) {
      form.setValue('needs_review', category === 'project', { shouldDirty: true })
    }
  }, [action, catalog.data, fields, form, serviceCode])

  const send = async (values: FormValues) => {
    setPending(null)
    setCovered(false)
    /*
     * The drawn values, and — for an override — the one field they are sent as.
     *
     * `widgets_set_config` takes `{ widget, config: { … } }` and the fields drawn
     * are `config`'s own properties, so the body is nested here rather than in the
     * page: a page that had to assemble it would be a page that knows what
     * `bodyOf` does to a numeric select, which is the thing this file exists to
     * know.
     */
    setOvertaken(null)
    setUnchanged(false)
    let drawn: Record<string, unknown>
    if (expects && opened !== undefined) {
      /*
       * A stored row other people edit too: what changed, and what it changed from
       * (`schemaForm.ts` `changesOf`). A box nobody touched is not sent back holding
       * what it held when the window opened, and a box that was changed says what it
       * was changed from, so the API can refuse it over somebody else's edit.
       */
      const { changes, was } = changesOf(fields, values, opened, clashed)
      if (Object.keys(changes).length === 0) {
        // Nothing to save is not a request: an empty update would still move the
        // task's version, and fail somebody else's Undo for a change nobody made.
        if (onUnchanged === undefined) setUnchanged(true)
        else onUnchanged()
        return
      }
      drawn = Object.keys(was).length === 0 ? changes : { ...changes, [WAS_FIELD]: was }
    } else {
      drawn = bodyOf(fields, values, opened)
    }
    const sent = override === undefined ? drawn : { [override.under]: drawn }
    /*
     * **The request, and only the request, is guarded.**
     *
     * The `catch` below is for one thing — the API said no — and it reads what it
     * caught as a problem document. `onDone` used to be called inside the same `try`,
     * so a page whose `onDone` threw (`CreatedSchema.parse(output)` over an answer
     * that had changed shape) landed in it too: an exception with no field paths and
     * no status, treated as a refusal, mapped onto nothing and gone. The write had
     * happened, the page's half had not, and nothing anywhere said so — in a bulk
     * edit the rest of the batch simply did not run.
     */
    let output: unknown
    onSending?.(true)
    try {
      output = await run.mutateAsync(
        runtimeBody({ ...sent, ...conditional(fixed, openedFrom, decided) }),
      )
    } catch (error: unknown) {
      if (error instanceof Unanswered) {
        const ref = fixed?.['task_ref']
        setUnanswered({ subject, taskRef: typeof ref === 'string' ? ref : null })
      }
      /*
       * Somebody else changed it first (`api/problem.ts` `changedByOthers`).
       *
       * Said under the box it is about, in a person's words — the API's own are for
       * a tool caller, who is told to read the task again; this reader has it on the
       * page behind the window, which is read again when the window shuts. What they
       * typed stays where it is. Where the refusal is about something this form does
       * not draw (who held the task, where it stood), it is one sentence at the foot
       * instead.
       *
       * "First", and not "while the window was open": the row the window was opened
       * from is up to a minute old, so theirs may have been saved before it opened.
       */
      const theirs = changedByOthers(error)
      if (theirs !== null) {
        const here = fields.filter((one) => theirs.includes(one.name))
        if (expects && here.length === theirs.length) {
          setClashed((before) => new Set([...before, ...here.map((one) => one.name)]))
          for (const one of here) {
            // A money box is two controls, and the amount is the one a sentence sits under.
            form.setError(one.widget === 'money' ? `${one.name}.major` : one.name, {
              type: 'overtaken',
              message:
                'Somebody else changed this first, so yours was not saved. Press ' +
                `${submitLabel} again to put yours in its place, or close the window to keep theirs.`,
            })
          }
        } else {
          setOvertaken({ about: subject, words: changedWords(error) ?? 'this' })
          if (insist === true) setDecidedFor(subject)
        }
        setCovered(true)
        return
      }
      /*
       * The API's own field errors, put back on the fields.
       *
       * `ARCHITECTURE.md` §Frontend: "Errors from the API's `errors` field map back
       * onto fields." `http/problem.ts` renders a `ZodError` as `[{ path, message }]`
       * and the path is dotted for a nested one (`amount.currency`), which is exactly
       * what react-hook-form's own `name` is — so a nested refusal lands under the
       * money box rather than as a sentence at the top of the form.
       *
       * `mutateAsync` rejects, and the rejection is already on `run.error` for the
       * notice below; this is the same object, read for its paths.
       */
      const onFields = fieldErrorsOf(error, new Set(fields.map((one) => one.name)), override?.under)
      for (const one of onFields) {
        form.setError(one.path, { type: 'server', message: one.message })
      }
      /*
       * The footer says nothing a field has already said.
       *
       * R3-09: a refusal landed under the box that caused it **and** as
       * "The request body is not valid." at the bottom of the form — a generic
       * sentence about a body the reader did not write, under a specific one they
       * could act on. The notice is for a refusal with no field to land on: a 403, a
       * conflict, a rule the form cannot express.
       */
      setCovered(onFields.length > 0 && onFields.length === problemPaths(error).length)
      return
    } finally {
      // Answered, either way — and before `onDone`, which is what shuts the window.
      onSending?.(false)
    }
    /*
     * Out here, a throw from the page's own `onDone` rejects this function's promise
     * and reaches nothing that would tidy it away — every caller of `send` is a
     * `void` — so it is an uncaught error in the console and a red test, which is
     * what a bug in a page is.
     */
    setMade(true)
    if (reviewOf(output) === null) onDone?.(output)
  }

  const submit: SubmitHandler<FormValues> = (values) => {
    // Enter in a box, while the button is waiting or has nothing to send: its own answer.
    if (working || stale) return
    if (money) {
      setPending(values)
      return
    }
    const ask = confirm?.(values) ?? null
    if (ask !== null) {
      setAsking({ values, ask })
      return
    }
    void send(values)
  }

  /*
   * The foot's sentence for the last press, where no field has said it. Out of date,
   * only that a press had no answer: anything else would be about pressing again, and
   * nothing will be sent from here.
   */
  const elsewhere = unanswered !== null && unanswered.subject !== subject ? unanswered : null
  const last =
    !run.isError || covered
      ? null
      : (unansweredSaid(run.error, submitLabel, { offer: !stale, elsewhere }) ??
        (stale ? null : messageOf(run.error)))
  /*
   * **And an earlier press that is still unknown**, where a later one was told something
   * else — never sent, signed out, refused for a box — for as long as the hook holds its
   * key (`unsettled`). A statement and no offer: whether a press here would be that one
   * is the hook's to know. Without it, a form holding a key — and Quick create's "More
   * details" shut for it — had nothing on it to say why.
   */
  const earlier =
    unsettled && unanswered !== null && !(run.error instanceof Unanswered)
      ? `Your earlier press of ${submitLabel}${elsewhere === null ? '' : onTask(elsewhere)} still has no answer, so it may or may not have been done.`
      : null
  const said = last === null ? earlier : earlier === null ? last : `${last} ${earlier}`

  const amount = pending === null ? null : amountIn(fields, pending)
  /*
   * The reference the confirmation names, from the form **or** from the page.
   *
   * A `$` button confirms with the amount and the reference (`.docs/20` §Design
   * decisions), and a dialog opened from a row has already answered the reference —
   * `fixed={{ work_order_ref: 'WO-2026-008' }}` — which means the field is not drawn
   * and `refIn` over the drawn fields finds nothing. Every money confirmation opened
   * from a row therefore said "Deliver $1,419.00" and named nothing, on precisely the
   * screens where two rows differ only by their reference.
   */
  const ref = pending === null ? null : (refIn(fields, pending) ?? refInFixed(fixed))

  /*
   * Takes "that is not a whole value yet" off a box that has since been emptied.
   * Only this form's own mark, and only once the browser agrees (`settledBox`): a
   * refusal the resolver or the API wrote is not this handler's to remove.
   */
  const settle = (event: { readonly target: EventTarget }): void => {
    const name = settledBox(event.target)
    if (name === null) return
    if (form.getFieldState(name).error?.type === HALF_TYPED_ERROR) form.clearErrors(name)
  }

  /*
   * The cursor in the first box, when the caller asked for it. A layout effect so it
   * lands in the same commit the dialog mounted in, before Radix's own autofocus has
   * settled on the Close button.
   */
  const formRef = useRef<HTMLFormElement>(null)
  useLayoutEffect(() => {
    if (autoFocusFirstField !== true) return
    const first = formRef.current?.querySelector<HTMLElement>('input, textarea, select')
    first?.focus()
  }, [autoFocusFirstField])

  return (
    <Stack>
      {children}
      {/*
        `handleSubmit` answers a promise, and `onSubmit` wants nothing back — `void`
        is the eslint-approved way of saying so.

        **A box that is half typed stops the form**, and is told why under itself
        (`Form.tsx` `halfTypedIn`). The form's own values cannot see one: such a box
        reports `''`, the resolver reads that as an optional field left blank, and
        `bodyOf` then sent it as the stored value being *taken away*. So the elements
        are asked, here, before anything is. The resolver still runs — a title left
        blank beside a half-typed date is refused in the same press — and the half
        typed boxes are marked after it, because `handleSubmit` replaces the form's
        errors with the resolver's and would wipe a mark made before it.

        The mark comes off when the box is finished — that is a change, and the
        resolver clears it — and when it is **emptied**, which is not one (`settle`).
      */}
      <form
        ref={formRef}
        noValidate
        onKeyUp={settle}
        onBlur={settle}
        onSubmit={(event) => {
          const half = halfTypedIn(event.currentTarget)
          const whole = half.length === 0
          void form
            .handleSubmit(whole ? submit : () => undefined)(event)
            .then(() => {
              half.forEach((one, index) => {
                form.setError(
                  one.name,
                  { type: HALF_TYPED_ERROR, message: one.message },
                  { shouldFocus: index === 0 },
                )
              })
            })
        }}
      >
        <Stack>
          {columns === 2 ? (
            <div className="aform-grid">
              {fields.map((field) => (
                <div
                  key={field.name}
                  className={span.has(field.name) ? 'aform-grid__span' : undefined}
                >
                  <ActionField
                    field={field}
                    control={form.control}
                    without={without?.[field.name]}
                  />
                </div>
              ))}
            </div>
          ) : (
            fields.map((field) => (
              <ActionField
                key={field.name}
                field={field}
                control={form.control}
                without={without?.[field.name]}
              />
            ))
          )}

          {review === null ? null : <NeedsReviewNotice review={review} />}
          {said === null ? null : (
            <Notice need role="alert">
              {said}
            </Notice>
          )}
          {overtaken === null ? null : (
            <Notice need role="alert">
              Somebody else changed {overtaken.words} first, so nothing was done.{' '}
              {insist === true && overtaken.about === subject
                ? `Press ${submitLabel} again to do it anyway, or close the window to look first.`
                : 'Close this window to see it as it is now.'}
            </Notice>
          )}
          {!stale ? null : (
            <Notice need role="alert">
              Your own change to {wordsFor(movedOn)} came through after this window was opened, so
              it is out of date and nothing will be sent from it. Close it and open it again from
              the task as it is now.
            </Notice>
          )}
          {unchanged ? (
            <Notice role="status">Nothing here was changed, so there is nothing to save.</Notice>
          ) : null}

          {asking !== null ? (
            <Notice need>
              <Stack>
                <Prose>
                  {asking.ask.title} {asking.ask.says}
                </Prose>
                <ButtonRow>
                  <Button
                    variant="ochre"
                    disabled={working || stale}
                    onClick={() => {
                      const values = asking.values
                      setAsking(null)
                      void send(values)
                    }}
                  >
                    {working ? 'Working…' : asking.ask.confirmLabel}
                  </Button>
                  <Button
                    variant="ghost"
                    id={notYetId}
                    onClick={() => {
                      setAsking(null)
                    }}
                  >
                    Not yet
                  </Button>
                </ButtonRow>
              </Stack>
            </Notice>
          ) : (
            <ButtonRow>
              <Button
                variant={submitVariant ?? 'primary'}
                type="submit"
                id={submitId}
                disabled={working || stale}
              >
                {working ? 'Working…' : submitLabel}
              </Button>
              {/*
                A second control beside the commit, for a form that has somewhere else
                to go — "More details" on quick create (#33). It belongs here rather
                than in `children`, which `Stack` draws *above* the fields, where a
                button reads as one more box to fill in.
              */}
              {typeof beside === 'function'
                ? beside(form.watch(), working || unsettled)
                : (beside ?? null)}
            </ButtonRow>
          )}
        </Stack>
      </form>

      {money ? (
        <ConfirmDialog
          open={pending !== null}
          onOpenChange={(open) => {
            if (!open) setPending(null)
          }}
          title={title}
          confirmLabel={confirmLabel(
            submitLabel,
            amount === null ? null : format.money(amount),
            ref,
          )}
          /*
           * "Not yet", never "Cancel".
           *
           * `ConfirmDialog` defaults its second button to *Cancel*, which is a
           * perfectly good word for "do not do this" until the thing being confirmed
           * is itself a cancellation: *Cancel it $0.00 against REQ-112* beside
           * *Cancel* is two buttons that read alike, one of which commits and one of
           * which does not. The dismiss cannot be worded from the verb — it is the
           * verb that is the problem — so it is worded from the timing instead, which
           * is true of every action this form draws. `QuoteBuilder` reached the same
           * answer for the same reason.
           */
          cancelLabel="Not yet"
          busy={run.isPending}
          onConfirm={() => {
            if (pending !== null) void send(pending)
          }}
        >
          <Prose>{commitSentence(title, amount === null ? null : format.money(amount), ref)}</Prose>
        </ConfirmDialog>
      ) : null}
    </Stack>
  )
}

/* ------------------------------------------------------- the API's own errors */

/*
 * `api/problem.ts` `fieldErrorsOf` is where a refusal's paths become field errors,
 * because the client's own widget-configuration form needs the identical rule — the
 * `errors[]`/`meta.path` pair, the narrowing to rendered controls, and the strip of
 * the action's own field. What is left in this file is which set of names to hand it.
 */

/* --------------------------------------------------------------- the words */

/**
 * "Record $520.00 against FES-2026-046".
 *
 * `.docs/20` §Design decisions: "Money actions confirm with the amount." The button
 * says what pressing it does, because a dialog whose button says "Confirm" is a
 * dialog people press without reading.
 */
export function confirmLabel(verb: string, amount: string | null, ref: string | null): string {
  const parts = [verb]
  if (amount !== null) parts.push(amount)
  if (ref !== null) parts.push(`against ${ref}`)
  return parts.join(' ')
}

/** The sentence above the two buttons. Never blank, whatever the input carries. */
export function commitSentence(title: string, amount: string | null, ref: string | null): string {
  if (amount === null && ref === null) {
    return `${title} commits money. Check the fields before it runs.`
  }
  const what = amount === null ? 'This one is about' : `This one is ${amount}`
  return ref === null
    ? `${title} commits money. ${what}.`
    : `${title} commits money. ${what}${amount === null ? ' ' : ' against '}${ref}.`
}

/**
 * The page's `fixed` fields, on the condition the window **opened** with
 * (`openedFrom`) rather than whatever the page would say it is now — and on none at
 * all once the reader has been told it no longer holds and has pressed again, where
 * the page lets that be insisted on (`insist`). `decided` is never true anywhere else.
 */
function conditional(
  fixed: Readonly<Record<string, unknown>> | undefined,
  openedFrom: unknown,
  decided: boolean,
): Readonly<Record<string, unknown>> {
  if (fixed === undefined) return {}
  const { [WAS_FIELD]: _now, ...rest } = fixed
  return decided || openedFrom === undefined ? rest : { ...rest, [WAS_FIELD]: openedFrom }
}

/* ------------------------------------------------------------- the values */

/**
 * The body, from the form's own state.
 *
 * `components/portal/schemaForm.ts` `wireValuesOf` is the whole of it except for one
 * action, and the exception is why this wrapper exists rather than the call being
 * inline: `tasks_assign`'s single control answers **two** of its fields, which is a
 * fact about an action and not about a shape.
 *
 * `opened` is `wireValuesOf`'s, passed straight through: what the boxes held when the
 * form was drawn, so that one the reader emptied is sent as a clear rather than left
 * out. The assignee is not part of that — its box has no blank to go back to, and
 * "nobody" is a choice in the list with a pair of its own.
 */
export function bodyOf(
  fields: readonly FieldSpec[],
  values: FormValues,
  opened?: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const assignees = fields.filter((field) => field.widget === 'assignee')
  const body = wireValuesOf(
    fields.filter((field) => field.widget !== 'assignee'),
    values,
    opened,
  )

  /*
   * The one widget that answers **two** of its action's fields.
   *
   * `tasks_assign` takes `assignee_kind` and `assignee_id`, and they are one
   * decision rather than two questions — its own `ValidationFailed` says so, and
   * the pair is what `tasks_list_assignees` answers with. `AssigneeField` holds it
   * as `"user:<uuid>"` or `"none"`, and this is where it comes apart again; the
   * illegal combinations cannot be expressed in the box at all.
   */
  for (const field of assignees) {
    const value = values[field.name]
    if (isBlank(value) || typeof value !== 'string') continue
    Object.assign(body, assigneeBody(value))
  }
  return body
}

/**
 * The assignee box's `"user:<uuid>"` or `"none"`, as the pair the API takes.
 *
 * `bodyOf` for a box that is drawn, and quick create for one it is not: New Task's
 * one-field window carries the board's Who filter as `fixed`, and `fixed` is merged
 * into the body as it stands — so the box's own value went out raw, as an `assignee`
 * key `tasks_create`'s strict input has never heard of, and a task could not be
 * raised from the quick form at all while the board was filtered to "me" or to
 * "Unassigned". One split, exported, rather than a second copy of it there.
 *
 * `"none"` comes apart as `{ assignee_kind: 'none', assignee_id: null }`, which is
 * what both `tasks_assign` and `tasks_create` take for nobody — "Null, and only
 * null, for none" is the published description of the second field on each.
 */
export function assigneeBody(value: string): {
  readonly assignee_kind: string
  readonly assignee_id: string | null
} {
  const [kind = NOBODY, id] = value.split(':')
  return { assignee_kind: kind, assignee_id: id ?? null }
}

/**
 * The other direction: a `Money` off a row, as the box that will hold it.
 *
 * Every `$` tool states the amount it commits and the service refuses a statement
 * that does not match the row (`CLAUDE.md` — "`amountOf` is **pure over the input**"),
 * so a page that has the row pre-fills the figure and the reader confirms it rather
 * than transcribing it from another screen. Exported because three of session 24's
 * work-order buttons need it and a fourth copy of `String(toMajorUnits(…))` is a
 * fourth chance to write `amount / 100`.
 *
 * `toMajorUnits` is `shared/money.ts`'s, which is the only file allowed to divide an
 * amount by a hundred.
 */
export function moneyFieldValue(amount: Money): { major: string; currency: Currency } {
  return { major: String(toMajorUnits(amount)), currency: amount.currency }
}

/** The first money field's value, for the confirm button. Null when there is none. */
function amountIn(fields: readonly FieldSpec[], values: FormValues): Money | null {
  for (const field of fields) {
    const value = values[field.name]
    if (field.widget === 'money' && isMoneyValue(value) && value.major.trim() !== '') {
      /*
       * `amountOf` is `schemaForm.ts`'s and is the one conversion from a typed box —
       * `fromMajorUnits` underneath it, because `1842.60 * 100` is exactly `184260`
       * and `8.29 * 100` is `828.9999999999999` (`CLAUDE.md` §Gotchas).
       */
      return amountOf(value.major, value.currency)
    }
  }
  return null
}

/**
 * The first reference the **page** answered for the form — `WO-2026-008`.
 *
 * `isRefField` is `schemaFields.ts`'s own rule (`ref`, or anything ending `_ref`), so
 * the two cannot disagree about what a reference is called. A `fixed` value that is
 * not a string is not one.
 */
function refInFixed(fixed: Readonly<Record<string, unknown>> | undefined): string | null {
  for (const [name, value] of Object.entries(fixed ?? {})) {
    if (isRefField(name) && typeof value === 'string' && value.trim() !== '') return value.trim()
  }
  return null
}

/** The first reference on the form — `FES-2026-046`. Null when the form names none. */
function refIn(fields: readonly FieldSpec[], values: FormValues): string | null {
  for (const field of fields) {
    const value = values[field.name]
    if (field.widget === 'ref' && typeof value === 'string' && value.trim() !== '') {
      return value.trim()
    }
  }
  return null
}
